/**
 * 繰り返す Tailwind のクラス列に名前を付ける。
 *
 * ブランドテーマ（紙とインク）のイディオムをここに集約する。太枠・硬い影を画面側で
 * 毎回書くと、1 箇所だけ 2px になっていても誰も気づけない。
 *
 * 値と役割の定義は `docs/DESIGN.md`。**生の hex をここにも画面にも書かない。**
 */

/**
 * インクの太枠。
 *
 * Tailwind v4 には線の太さのテーマ名前空間が無いため任意値で書く。
 * 3px は `brandBorder.md`（docs/DESIGN.md §3）。
 */
const inkBorder = 'border-[3px] border-ink';

/** カードの面。紙の上に浮いた 1 枚。 */
export const card = `rounded-lg ${inkBorder} bg-surface shadow-lg`;

export const panel = `${card} p-5`;

/** 沈んだ面。溝として使う（帯・能力値の地）。 */
export const sunken = `rounded-sm border-[2.5px] border-ink bg-sunken`;

export const heading = 'font-display text-2xl sm:text-3xl';

export const sectionTitle = 'font-display text-sm tracking-wide text-text-muted';

export const lead = 'text-text-muted leading-relaxed';

/** ラベル。系統は黄、特性は赤、その他は白地（docs/DESIGN.md §2）。 */
export const badge =
  'inline-block rounded-[14px] border-[2.5px] border-ink px-2 py-0.5 text-[11.5px]';

export const badgeYellow = `${badge} bg-yellow`;

export const badgeRed = `${badge} bg-red text-text-on-fill`;

/** ヘッダー・フッター・トップの中身の幅（`drawgon 公開サイト` のデザイン）。左右の余白は画面の幅で伸び縮みする。 */
export const siteContainer = 'mx-auto w-full max-w-[1200px] px-[clamp(16px,4vw,40px)]';

/** 開催中の面（design system の `.card-live`）。黄の地のカード。 */
export const cardLive = `rounded-lg ${inkBorder} bg-yellow shadow-lg`;

/**
 * 傾き。手書き感はここだけで出す（`brandTilt`、docs/DESIGN.md §3）。**1 度を超えない。**
 * Tailwind はクラス名を文字列のまま拾うので、値を組み立てずに書く（一致は `ui.test.ts` が見る）。
 */
export const tiltLeft = 'rotate-[-0.6deg]';
export const tiltRight = 'rotate-[0.5deg]';

/**
 * 押せるものの共通の挙動。**押したら影の分だけ沈む**（docs/DESIGN.md §3）。
 * 影の大きさはボタンごとに違うので、沈む量はそれぞれのクラスに書く。
 */
const pressable =
  'cursor-pointer focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-blue';

/** 小さく押せるもの（`.btn` / `.dial` / `.chip-action`）が沈む量。影 `shadow-sm` と同じ。 */
const sinkSm = 'active:translate-x-[2px] active:translate-y-[2px] active:shadow-none';

/** 主要 CTA（design system の `.btn-primary`）。**べた塗りの赤なので、画面に 1 つだけ。** */
export const btnPrimary = `${pressable} inline-flex items-center justify-center gap-2.5 rounded-lg ${inkBorder} bg-red p-4 font-display text-xl text-text-on-fill shadow-lg active:translate-x-[3px] active:translate-y-[4px] active:shadow-[1px_1px_0_var(--color-ink)]`;

/** 押せるチップ（design system の `.chip.chip-action`）。ヘッダーの「ランキング」。 */
export const chipAction = `${pressable} ${sinkSm} inline-block rounded-pill border-[2.5px] border-ink bg-surface px-3.5 py-[7px] shadow-sm`;

/** 丸いボタン（design system の `.dial`）。フッターの X。 */
export const dial = `${pressable} ${sinkSm} inline-flex size-[34px] items-center justify-center rounded-full border-[2.5px] border-ink bg-surface shadow-sm`;

/** 文字のリンク（フッター・本文）。線は 2px、文字から 4px 離す。 */
export const textLink = 'underline decoration-2 underline-offset-4 hover:text-blue-dk';

/**
 * ワードマーク「drawgon」。**黄色の硬い影を文字の大きさの 1 割ずらし、字間は 1px**（docs/DESIGN.md §4）。
 * 大きさは使う側で決める（影は `em` なので一緒に大きくなる）。
 */
export const wordmark =
  'font-display leading-none tracking-[1px] [text-shadow:0.1em_0.1em_0_var(--color-yellow)]';

/** 鑑定の痕跡のバッジ（design system の `.badge-ai`）。**等幅はここ専用**（docs/DESIGN.md §4）。 */
export const badgeAi = 'rounded-[4px] bg-ink px-[7px] py-[3px] font-mono text-[9px] text-lime';

/**
 * 画像の置き場所（design system の `.slot`）。青の地に、面の白を 2 割まぜた斜線。
 * スキャンラインは `index.css` の `.scan-area`。
 */
export const mediaSlot =
  'relative overflow-hidden rounded-md bg-blue bg-[image:repeating-linear-gradient(135deg,color-mix(in_srgb,var(--color-surface)_20%,transparent)_0_8px,transparent_8px_16px)]';

/**
 * エラー。**淡い地 + 濃い赤の文字 + インクの太枠。**
 *
 * べた塗りの赤は主要 CTA の専有なので、エラーは面を塗らない。
 * 塗られていないことが CTA との区別になる（docs/DESIGN.md §2）。
 */
export const alert = `rounded-md ${inkBorder} bg-red-soft px-4 py-3 shadow-sm`;

export const alertTitle = 'font-display text-danger';

/** トーナメント表の 1 人ぶんの行（#151）。長い名前でも折り返して崩れないようにする。 */
export const bracketSlot = 'text-sm leading-snug break-words';
